"use client";

import React, { Suspense, useEffect, useMemo, useState } from "react";

import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import { Input } from "@/stories/input/input";
import SearchIcon from "@/app/assets/icons/search";

import UsStocksHeroCard from "@/app/global/components/us-stocks-hero-card";
import { UsStock, UsStockListApiResponse } from "@/app/interface/global";
import { useUsStockSearchQuery, MIN_US_STOCK_SEARCH_LENGTH } from "@/app/global/use-us-stocks";
import {
    generalProdctType,
    getRouteWiseProduct,
    productRoute,
    ProductRoute,
} from "@/types/product";
import { toTitleCase } from "@/app/utils/helper";

interface Props {
    redirectTo?: string;
    stockList: UsStockListApiResponse | null;
    pageRoute?: ProductRoute;
}

const USStocksHero: React.FC<Props> = ({ /* redirectTo, */ stockList, pageRoute }) => {
    const minShowCount = 6;
    const [showAll, setShowAll] = useState<number>(minShowCount);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");

    const productType = pageRoute
        ? getRouteWiseProduct({ route: pageRoute })
        : generalProdctType.STOCK;

    const { usStockSearchResult, isUsStockSearchLoading } = useUsStockSearchQuery({
        query: debouncedQuery,
        type: productType,
    });

    useEffect(() => {
        const timer = setTimeout(() => {
            const trimmed = searchQuery.trim();
            setDebouncedQuery(trimmed.length >= MIN_US_STOCK_SEARCH_LENGTH ? trimmed : "");
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const isSearchActive = debouncedQuery.length >= MIN_US_STOCK_SEARCH_LENGTH;

    useEffect(() => {
        setShowAll(minShowCount);
    }, [debouncedQuery]);

    const activeList = useMemo(() => {
        if (isSearchActive) {
            return usStockSearchResult;
        }
        return stockList;
    }, [isSearchActive, usStockSearchResult, stockList]);

    const getFilteredData = useMemo(() => {
        if (activeList) {
            const filteredData = activeList?.collection.filter(
                (item: UsStock) => item, //item?.category === ListingCategory.LIVE,
            );
            return filteredData.slice(0, showAll);
        }
    }, [activeList, showAll]);

    // const { isMobile } = useMobile()

    return (
        <div className="mt-s15 flex flex-col items-center justify-center align-middle md:mt-20">
            {" "}
            {/* mb-s11 */}
            <h1 className="mb-3 text-center text-5xl font-semibold leading-[130%] tracking-[-0.4px] md:mb-5 md:text-[68px] md:leading-[1.2] md:tracking-[-1.8px]">
                Investing in US{" "}
                {pageRoute === productRoute["us-stocks"]
                    ? toTitleCase(getRouteWiseProduct({ route: pageRoute! })) + "s"
                    : pageRoute === productRoute["us-etfs"] && generalProdctType.ETF + "s"}{" "}
                from
                <br />
                India Online
            </h1>
            <div className="text-center">
                <p className="text-base md:text-3xl md:leading-[39.84px]">
                    {pageRoute === productRoute["us-stocks"]
                        ? "Open a free US Stocks account in minutes, add funds, and start investing in global."
                        : (pageRoute === productRoute["us-etfs"] &&
                              "A simpler way to access global markets through diversified investment baskets.") ||
                          ""}
                    <br />
                    <span className="font-bold md:text-2xl">Start Investing with Just $1</span>
                </p>
            </div>
            <div className="mt-s10 w-full max-w-[475px] px-4 sm:mt-s9 md:mt-s15 md:px-0">
                <Input
                    placeholder={
                        pageRoute === productRoute["us-etfs"]
                            ? `Search US ${generalProdctType.ETF}s`
                            : `Search US ${toTitleCase(generalProdctType.STOCK)}s`
                    }
                    minLength={MIN_US_STOCK_SEARCH_LENGTH}
                    icon={<SearchIcon size={14} className="py-1 pl-1 text-neutral-300" />}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    inputStyles="md:text-base placeholder:text-neutral-300"
                    className="h-11 w-full rounded-full border border-neutral-100 px-4 sm:h-14 sm:min-w-[475px]"
                />
                {searchQuery.trim().length > 0 &&
                    searchQuery.trim().length < MIN_US_STOCK_SEARCH_LENGTH && (
                        <p className="mt-2 text-center text-sm text-neutral-300">
                            Enter at least {MIN_US_STOCK_SEARCH_LENGTH} characters to search
                        </p>
                    )}
            </div>
            <div className="mt-s9 grid w-full grid-cols-1 gap-[8px] md:my-16 md:mb-10 md:mt-s12 md:w-max md:grid-cols-2 md:gap-4">
                <Suspense fallback={<Loader />}>
                    {isUsStockSearchLoading && isSearchActive ? (
                        <Loader />
                    ) : (
                        <UsStocksHeroCard data={getFilteredData} pageRoute={pageRoute} />
                    )}
                </Suspense>
            </div>
            {isSearchActive && !isUsStockSearchLoading && !getFilteredData?.length && (
                <p className="mb-s22 text-center text-lg font-medium text-neutral-300">
                    {`No ${pageRoute === productRoute["us-stocks"] ? generalProdctType.STOCK.toLowerCase() + "s" : (pageRoute === productRoute["us-etfs"] && generalProdctType.ETF) || "stock"} found matching your search.`}
                </p>
            )}
            {/* <Link href={redirectTo}> */}
            {getFilteredData && getFilteredData?.length >= minShowCount && (
                <RegularButton
                    noIcon
                    showRightIcon={false}
                    size={ButtonSize.Large}
                    variant={ButtonVariant.SecondaryDefault}
                    className="mb-s14 mt-8 w-full max-w-s26 md:mb-s20 md:mt-0 md:w-auto"
                    label={showAll === minShowCount ? "View More" : "View Less"}
                    onClick={() => {
                        setShowAll(
                            showAll === minShowCount
                                ? Number(activeList?.collection.length)
                                : minShowCount,
                        );
                    }}
                />
            )}
            {/* </Link> */}
            {/* <div className="text-center">
                <h3 className="text-base font-medium text-neutral-300 md:text-base">IN THE NEWS</h3>
                <EmblaCarousel
                    data={feedbacks.map((feedback, index) => (
                        <div key={index}>
                            <p className="text-xl font-semibold md:text-3xl">
                                {feedback.text}
                            </p>
                            <p className="text-base font-medium text-neutral-300 md:text-lg">
                                {feedback.name}
                            </p>
                        </div>
                    ))}
                    showDots={true}
                    showPrevOrNext={false}
                    autoPlay={true}
                />
            </div> */}
        </div>
    );
};

const Loader = () => {
    return Array.from({ length: 8 }).map((_, index) => (
        <div
            key={index}
            className="bg-red w-[300px] animate-pulse rounded-2xl border-2 border-gray-200 p-4 md:w-[442px] md:p-6"
        >
            <div className="flex gap-4 bg-gray-200">
                {/* Image container with fixed dimensions */}
                <div className="h-12 w-12 flex-shrink-0 md:h-20 md:w-20"></div>

                {/* Content wrapper with proper flex behavior */}
                <div className="flex min-w-0 flex-1 items-center justify-between">
                    {/* Text content with proper truncation */}
                    <div className="flex min-w-0 max-w-[calc(100%-40px)] flex-col gap-2">
                        <div className="overflow-hidden">
                            <h3 className="truncate text-sm font-semibold md:text-lg"></h3>
                        </div>
                        <div className="whitespace-nowrap text-sm">
                            <span className="text-gray-400"></span>
                        </div>
                    </div>

                    {/* Button with fixed width */}
                    <div className="flex-shrink-0"></div>
                </div>
            </div>
        </div>
    ));
};

export default USStocksHero;
